import { toast } from 'sonner';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  RefreshCw, LogOut, Search, ChevronDown, ChevronRight, Package, CreditCard,
  User as UserIcon, MapPin, Mail, Send, Phone, Trash2, Filter, ShoppingBag, Boxes, Layers,
  Truck, Check, Folder, CreditCard as CreditCardIcon, Package as PackageIcon, Globe, Ticket, FileText, Settings as SettingsIcon, Handshake, MessagesSquare, Star as StarIcon,
} from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import ProductsPanel from './ProductsPanel';
import StacksPanel from './StacksPanel';
import CategoriesPanel from './CategoriesPanel';
import KitPanel from './KitPanel';
import PaymentsPanel from './PaymentsPanel';
import ShippingPanel from './ShippingPanel';
import CountriesPanel from './CountriesPanel';
import CouponsPanel from './CouponsPanel';
import PagesPanel from './PagesPanel';
import SiteSettingsPanel from './SiteSettingsPanel';
import PartnersPanel from './PartnersPanel';
import MessagesPanel from './MessagesPanel';
import ReviewsPanel from './ReviewsPanel';
import { ReviewRequestButton } from './ReviewRequestButton';
import { AdminOrderPortal } from './order/AdminOrderPortal';
import { CopyButton } from './CopyButton';
import { ShipmentsPanel } from './ShipmentsPanel';
import { Pagination } from './Pagination';
import { SYMBOLS } from '../context/CurrencyContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending:   { label: 'Pending',   bg: 'bg-amber-100',   text: 'text-amber-700',   dot: 'bg-amber-500' },
  paid:      { label: 'Paid',      bg: 'bg-blue-100',    text: 'text-blue-700',    dot: 'bg-blue-500' },
  shipped:   { label: 'Shipped',   bg: 'bg-indigo-100',  text: 'text-indigo-700',  dot: 'bg-indigo-500' },
  delivered: { label: 'Delivered', bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  cancelled: { label: 'Cancelled', bg: 'bg-neutral-200', text: 'text-neutral-600', dot: 'bg-neutral-400' },
};
const STATUSES = ['pending', 'paid', 'shipped', 'delivered', 'cancelled'];

const formatDate = (s) => {
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

const StatCard = ({ label, value, sub }) => (
  <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] p-4">
    <div className="text-[11px] tracking-widest uppercase text-neutral-500">{label}</div>
    <div className="mt-1 text-[24px] font-semibold text-neutral-900 dark:text-neutral-100">{value}</div>
    {sub && <div className="text-[11.5px] text-neutral-500 mt-0.5">{sub}</div>}
  </div>
);

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11.5px] font-medium ${m.bg} ${m.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
};

const AdminDashboard = () => {
  const { password, logout } = useAdminAuth();
  const [tab, setTab] = useState('orders');
  const [helpUnread, setHelpUnread] = useState(0);
  useEffect(() => {
    if (!password) return undefined;
    const poll = () => axios.get(`${API}/admin/help/unread`, { headers: { 'X-Admin-Password': password } }).then(({ data }) => setHelpUnread(data.unread)).catch(() => {});
    poll();
    const t = setInterval(poll, 30000);
    return () => clearInterval(t);
  }, [password]);
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [expanded, setExpanded] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [query, setQuery] = useState('');

  const headers = useMemo(() => ({ 'X-Admin-Password': password }), [password]);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [oRes, sRes] = await Promise.all([
        axios.get(`${API}/orders`, { headers }),
        axios.get(`${API}/orders/summary`, { headers }),
      ]);
      setOrders(oRes.data || []);
      setSummary(sRes.data);
    } catch (e) {
      if (e?.response?.status === 401) { logout(); return; }
      setErr('Failed to load orders. Try again.');
    } finally { setLoading(false); }
  }, [headers, logout]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      const res = await axios.patch(`${API}/orders/${id}`, { status }, { headers });
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status } : o));
      if (res.data?.email_dispatched) {
        toast.success(res.data.email_configured
          ? `Customer notified: order marked ${status}.`
          : `Status set to ${status}. SMTP not configured — email skipped.`);
      }
      load();
    } catch { setErr('Failed to update status.'); }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm(`Delete order ${id}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/orders/${id}`, { headers });
      setOrders(prev => prev.filter(o => o.id !== id));
      load();
    } catch { setErr('Failed to delete order.'); }
  };

  const filtered = useMemo(() => {
    let l = orders;
    if (statusFilter !== 'all') l = l.filter(o => (o.status || 'pending') === statusFilter);
    const q = query.trim().toLowerCase();
    if (q) {
      l = l.filter(o => {
        const f = o.form || {};
        return (
          (o.id || '').toLowerCase().includes(q) ||
          (f.fullName || '').toLowerCase().includes(q) ||
          (f.email || '').toLowerCase().includes(q) ||
          (f.city || '').toLowerCase().includes(q) ||
          (o.network || '').toLowerCase().includes(q)
        );
      });
    }
    return l;
  }, [orders, statusFilter, query]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  useEffect(() => { setPage(1); }, [statusFilter, query, pageSize]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);
  const pageRows = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);
  const goPage = (n) => { setPage(n); document.querySelector('[data-testid=orders-table]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  return (
    <div className="min-h-screen bg-[#F8F8F6] dark:bg-[#0F0F10]">
      {/* Top bar */}
      <header className="sticky top-0 z-10 bg-white/85 dark:bg-[#0F0F10]/85 backdrop-blur border-b border-neutral-200 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div>
            <div className="font-serif text-[22px] leading-none text-neutral-900 dark:text-neutral-100">Healsglobe Exports · Admin</div>
            <div className="text-[12px] text-neutral-500 mt-1">Orders & products dashboard</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="inline-flex items-center gap-1.5 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-1.5 text-[13px] hover:bg-neutral-50 dark:hover:bg-neutral-800">
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={logout} className="inline-flex items-center gap-1.5 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-1.5 text-[13px] hover:bg-neutral-50 dark:hover:bg-neutral-800">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap gap-x-1 [&>button]:whitespace-nowrap [&>button]:px-3">
          <button data-testid="tab-orders" onClick={() => setTab('orders')} className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[13.5px] border-b-2 transition-colors ${tab === 'orders' ? 'border-neutral-900 dark:border-neutral-100 text-neutral-900 dark:text-neutral-100 font-medium' : 'border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'}`}>
            <ShoppingBag size={14} /> Orders
          </button>
          <button data-testid="tab-products" onClick={() => setTab('products')} className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[13.5px] border-b-2 transition-colors ${tab === 'products' ? 'border-neutral-900 dark:border-neutral-100 text-neutral-900 dark:text-neutral-100 font-medium' : 'border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'}`}>
            <Boxes size={14} /> Products
          </button>
          <button data-testid="tab-stacks" onClick={() => setTab('stacks')} className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[13.5px] border-b-2 transition-colors ${tab === 'stacks' ? 'border-neutral-900 dark:border-neutral-100 text-neutral-900 dark:text-neutral-100 font-medium' : 'border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'}`}>
            <Layers size={14} /> Stacks
          </button>
          <button data-testid="tab-categories" onClick={() => setTab('categories')} className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[13.5px] border-b-2 transition-colors ${tab === 'categories' ? 'border-neutral-900 dark:border-neutral-100 text-neutral-900 dark:text-neutral-100 font-medium' : 'border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'}`}>
            <Folder size={14} /> Categories
          </button>
          <button data-testid="tab-kit" onClick={() => setTab('kit')} className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[13.5px] border-b-2 transition-colors ${tab === 'kit' ? 'border-neutral-900 dark:border-neutral-100 text-neutral-900 dark:text-neutral-100 font-medium' : 'border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'}`}>
            <PackageIcon size={14} /> Kit
          </button>
          <button data-testid="tab-payments" onClick={() => setTab('payments')} className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[13.5px] border-b-2 transition-colors ${tab === 'payments' ? 'border-neutral-900 dark:border-neutral-100 text-neutral-900 dark:text-neutral-100 font-medium' : 'border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'}`}>
            <CreditCardIcon size={14} /> Payments
          </button>
          <button data-testid="tab-shipping" onClick={() => setTab('shipping')} className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[13.5px] border-b-2 transition-colors ${tab === 'shipping' ? 'border-neutral-900 dark:border-neutral-100 text-neutral-900 dark:text-neutral-100 font-medium' : 'border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'}`}>
            <Truck size={14} /> Shipping
          </button>
          <button data-testid="tab-countries" onClick={() => setTab('countries')} className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[13.5px] border-b-2 transition-colors ${tab === 'countries' ? 'border-neutral-900 dark:border-neutral-100 text-neutral-900 dark:text-neutral-100 font-medium' : 'border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'}`}>
            <Globe size={14} /> Countries
          </button>
          <button data-testid="tab-coupons" onClick={() => setTab('coupons')} className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[13.5px] border-b-2 transition-colors ${tab === 'coupons' ? 'border-neutral-900 dark:border-neutral-100 text-neutral-900 dark:text-neutral-100 font-medium' : 'border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'}`}>
            <Ticket size={14} /> Coupons
          </button>
          <button data-testid="tab-pages" onClick={() => setTab('pages')} className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[13.5px] border-b-2 transition-colors ${tab === 'pages' ? 'border-neutral-900 dark:border-neutral-100 text-neutral-900 dark:text-neutral-100 font-medium' : 'border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'}`}>
            <FileText size={14} /> Pages
          </button>
          <button data-testid="tab-messages" onClick={() => setTab('messages')} className={`relative inline-flex items-center gap-1.5 px-4 py-2.5 text-[13.5px] border-b-2 transition-colors ${tab === 'messages' ? 'border-neutral-900 dark:border-neutral-100 text-neutral-900 dark:text-neutral-100 font-medium' : 'border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'}`}>
            <MessagesSquare size={14} /> Messages
            {helpUnread > 0 && <span data-testid="tab-messages-unread" className="ml-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-600 text-white text-[10.5px] grid place-items-center">{helpUnread}</span>}
          </button>
          <button data-testid="tab-reviews" onClick={() => setTab('reviews')} className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[13.5px] border-b-2 transition-colors ${tab === 'reviews' ? 'border-neutral-900 dark:border-neutral-100 text-neutral-900 dark:text-neutral-100 font-medium' : 'border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'}`}>
            <StarIcon size={14} /> Reviews
          </button>
          <button data-testid="tab-partners" onClick={() => setTab('partners')} className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[13.5px] border-b-2 transition-colors ${tab === 'partners' ? 'border-neutral-900 dark:border-neutral-100 text-neutral-900 dark:text-neutral-100 font-medium' : 'border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'}`}>
            <Handshake size={14} /> Partners
          </button>
          <button data-testid="tab-settings" onClick={() => setTab('settings')} className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[13.5px] border-b-2 transition-colors ${tab === 'settings' ? 'border-neutral-900 dark:border-neutral-100 text-neutral-900 dark:text-neutral-100 font-medium' : 'border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'}`}>
            <SettingsIcon size={14} /> Site Settings
          </button>
        </div>
      </header>

      {tab === 'products' ? (
        <main className="max-w-7xl mx-auto px-6 py-8">
          <ProductsPanel />
        </main>
      ) : tab === 'stacks' ? (
        <main className="max-w-7xl mx-auto px-6 py-8">
          <StacksPanel />
        </main>
      ) : tab === 'categories' ? (
        <main className="max-w-7xl mx-auto px-6 py-8">
          <CategoriesPanel />
        </main>
      ) : tab === 'kit' ? (
        <main className="max-w-7xl mx-auto px-6 py-8">
          <KitPanel />
        </main>
      ) : tab === 'payments' ? (
        <main className="max-w-7xl mx-auto px-6 py-8">
          <PaymentsPanel />
        </main>
      ) : tab === 'shipping' ? (
        <main className="max-w-7xl mx-auto px-6 py-8">
          <ShippingPanel />
        </main>
      ) : tab === 'countries' ? (
        <main className="max-w-7xl mx-auto px-6 py-8">
          <CountriesPanel />
        </main>
      ) : tab === 'coupons' ? (
        <main className="max-w-7xl mx-auto px-6 py-8">
          <CouponsPanel />
        </main>
      ) : tab === 'pages' ? (
        <main className="max-w-7xl mx-auto px-6 py-8">
          <PagesPanel />
        </main>
      ) : tab === 'messages' ? (
        <main className="max-w-7xl mx-auto px-6 py-8">
          <MessagesPanel onUnreadChange={setHelpUnread} />
        </main>
      ) : tab === 'reviews' ? (
        <main className="max-w-7xl mx-auto px-6 py-8">
          <ReviewsPanel />
        </main>
      ) : tab === 'partners' ? (
        <main className="max-w-7xl mx-auto px-6 py-8">
          <PartnersPanel />
        </main>
      ) : tab === 'settings' ? (
        <main className="max-w-7xl mx-auto px-6 py-8">
          <SiteSettingsPanel />
        </main>
      ) : (
      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <StatCard label="Total orders" value={summary?.total_orders ?? '—'} />
          <StatCard label="Revenue" value={summary ? `$${summary.total_revenue.toFixed(2)}` : '—'} sub="Paid + Shipped + Delivered" />
          {STATUSES.map(s => (
            <StatCard key={s} label={STATUS_META[s].label} value={summary?.counts?.[s] ?? 0} />
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by order ID, name, email, city, network…"
              className="w-full bg-white dark:bg-[#141416] border border-neutral-200 dark:border-neutral-800 rounded-md pl-9 pr-3 py-2.5 text-[14px] outline-none focus:border-neutral-400"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-[12.5px] text-neutral-500"><Filter size={13} /> Status</span>
            {[['all','All'], ...STATUSES.map(s => [s, STATUS_META[s].label])].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setStatusFilter(k)}
                className={`px-3 py-1.5 rounded-full text-[12.5px] border transition-colors ${statusFilter === k ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white dark:bg-[#141416] border-neutral-200 dark:border-neutral-800 text-neutral-700 dark:text-neutral-300 hover:border-neutral-400'}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {err && <div className="text-red-600 text-[13px]">{err}</div>}

        {/* Orders table */}
        <div data-testid="orders-table" style={{ scrollMarginTop: 140 }} className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] overflow-hidden">
          <div className="grid grid-cols-[24px_1.2fr_1.6fr_1fr_0.9fr_0.9fr_1.2fr_60px] gap-3 px-4 py-3 text-[11px] tracking-widest uppercase text-neutral-500 border-b border-neutral-200 dark:border-neutral-800">
            <div></div>
            <div>Order</div>
            <div>Customer</div>
            <div>Payment</div>
            <div>Total</div>
            <div>Status</div>
            <div>Actions</div>
            <div></div>
          </div>

          {loading && orders.length === 0 && (
            <div className="py-16 text-center text-neutral-500 text-[13.5px]">Loading orders…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="py-16 text-center text-neutral-500 text-[13.5px]">No orders match your filters.</div>
          )}

          {pageRows.map(o => {
            const f = o.form || {};
            const items = o.items || [];
            const isOpen = !!expanded[o.id];
            return (
              <div key={o.id} className="border-b border-neutral-100 dark:border-neutral-800 last:border-b-0">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggle(o.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(o.id); } }}
                  className="w-full grid grid-cols-[24px_1.2fr_1.6fr_1fr_0.9fr_0.9fr_1.2fr_60px] gap-3 px-4 py-3.5 items-center text-left hover:bg-neutral-50 dark:hover:bg-neutral-900 cursor-pointer"
                >
                  <div className="text-neutral-400">
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </div>
                  <div>
                    <div className="font-mono text-[13px] text-neutral-900 dark:text-neutral-100">{o.id}</div>
                    <div className="text-[11.5px] text-neutral-500 mt-0.5">{formatDate(o.at)}</div>
                    {o.tracking_number && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10.5px] text-emerald-600" title={o.tracking_number}>
                        <Truck size={10} /> <span className="font-mono truncate max-w-[130px]">{o.tracking_number}</span>
                        {(o.shipments || []).length > 1 && <span data-testid={`shipments-count-${o.id}`} className="font-semibold">+{o.shipments.length - 1}</span>}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-[13.5px] text-neutral-900 dark:text-neutral-100 truncate">{f.fullName || '—'}</div>
                    <div className="text-[12px] text-neutral-500 truncate">{f.email || ''} {f.city ? '· ' + f.city : ''}</div>
                  </div>
                  <div className="text-[13px] text-neutral-700 dark:text-neutral-300 truncate">{o.network || '—'}</div>
                  <div className="text-[13.5px] font-medium text-neutral-900 dark:text-neutral-100">${Number(o.total || 0).toFixed(2)}
                    {o.currency && o.currency !== 'USD' && o.local_totals && <div data-testid={`order-local-total-${o.id}`} className="text-[11px] font-normal text-neutral-500">{SYMBOLS[o.currency]}{Number(o.local_totals.total).toFixed(2)} {o.currency}</div>}
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <StatusBadge status={o.status || 'pending'} />
                    {o.review_requested_at && (
                      <span data-testid={`review-requested-pill-${o.id}`} title={`Review requested ${formatDate(o.review_requested_at)}`}
                        className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 text-[10.5px] font-medium whitespace-nowrap">
                        <StarIcon size={10} className="fill-current" /> Review requested
                      </span>
                    )}
                    {o.edit_request_pending && <span data-testid={`edit-pending-pill-${o.id}`} className="rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 px-2 py-0.5 text-[10.5px] font-medium whitespace-nowrap">Change requested</span>}
                    {(o.payment_proofs || []).length > 0 && <span data-testid={`proof-pill-${o.id}`} className={`rounded-full px-2 py-0.5 text-[10.5px] font-medium whitespace-nowrap ${o.payment_proof_new ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'}`}>{o.payment_proof_new ? 'New payment proof' : 'Payment proof'}</span>}
                    {o.unread_admin_msgs > 0 && <span data-testid={`unread-msgs-pill-${o.id}`} className="rounded-full bg-emerald-600 text-white px-2 py-0.5 text-[10.5px] font-medium whitespace-nowrap">{o.unread_admin_msgs} new message{o.unread_admin_msgs > 1 ? 's' : ''}</span>}
                  </div>
                  <div onClick={(e) => e.stopPropagation()} className="flex flex-wrap gap-1">
                    <select
                      value={o.status || 'pending'}
                      onChange={(e) => updateStatus(o.id, e.target.value)}
                      className="text-[12px] bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-md px-2 py-1"
                    >
                      {STATUSES.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                    </select>
                  </div>
                  <div onClick={(e) => e.stopPropagation()} className="flex justify-end">
                    <button onClick={() => deleteOrder(o.id)} className="text-neutral-400 hover:text-red-500 p-1" title="Delete">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div className="px-4 pb-6 pt-1 grid grid-cols-1 md:grid-cols-2 gap-6 bg-neutral-50/50 dark:bg-neutral-900/40">
                    {/* Shipping / customer */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-[11px] tracking-widest uppercase text-neutral-500">Shipping details</h4>
                        <CopyButton testId={`copy-address-${o.id}`} label="Copy name & address"
                          text={[f.fullName, [f.address, f.apt].filter(Boolean).join(', '), [f.city, f.state, f.postalCode].filter(Boolean).join(', '), f.country].filter(Boolean).join('\n')} />
                      </div>
                      <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] p-4 space-y-2 text-[13.5px]">
                        <div className="flex gap-2"><UserIcon size={14} className="text-neutral-400 mt-0.5" /> <span className="text-neutral-900 dark:text-neutral-100">{f.fullName || '—'}</span></div>
                        <div className="flex gap-2"><MapPin size={14} className="text-neutral-400 mt-0.5" />
                          <div className="text-neutral-700 dark:text-neutral-200">
                            {[f.address, f.apt].filter(Boolean).join(', ')}<br />
                            {[f.city, f.state, f.postalCode].filter(Boolean).join(', ')}<br />
                            {f.country}
                          </div>
                        </div>
                        <div className="flex gap-2"><Mail size={14} className="text-neutral-400 mt-0.5" /> <a href={`mailto:${f.email}`} className="text-neutral-800 dark:text-neutral-200 underline underline-offset-2">{f.email || '—'}</a></div>
                        {f.telegram && (
                          <div className="flex gap-2"><Send size={14} className="text-neutral-400 mt-0.5" /> <span>{f.telegram}</span></div>
                        )}
                      </div>
                      <h4 className="text-[11px] tracking-widest uppercase text-neutral-500 mt-4 mb-2">Payment</h4>
                      <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] p-4 text-[13.5px]">
                        <div className="flex items-center gap-2"><CreditCard size={14} className="text-neutral-400" /> <span className="text-neutral-900 dark:text-neutral-100">{o.network}</span></div>
                        {(o.payment_proofs || []).filter(p => p.transaction_id).map(p => (
                          <div key={p.id} className="mt-1.5 text-[12px] text-neutral-500">Txn ID: <span data-testid={`order-txn-${o.id}`} className="font-mono text-neutral-900 dark:text-neutral-100 break-all">{p.transaction_id}</span></div>
                        ))}
                      </div>

                      <h4 className="text-[11px] tracking-widest uppercase text-neutral-500 mt-4 mb-2 flex items-center gap-2">
                        <Truck size={13} /> Shipment tracking{(o.shipments || []).length > 1 ? ` (${o.shipments.length} packages)` : ''}
                      </h4>
                      <ShipmentsPanel order={o} headers={headers}
                        onChange={(patch) => setOrders(prev => prev.map(x => (x.id === o.id ? { ...x, ...patch } : x)))} />

                      <h4 className="text-[11px] tracking-widest uppercase text-neutral-500 mt-4 mb-2 flex items-center gap-2">
                        <StarIcon size={13} /> Customer review
                      </h4>
                      <ReviewRequestButton order={o} headers={headers}
                        onSent={(id, at) => setOrders(prev => prev.map(x => (x.id === id ? { ...x, review_requested_at: at } : x)))} />
                    </div>

                    {/* Items + totals */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-[11px] tracking-widest uppercase text-neutral-500">Items ({items.length})</h4>
                        <CopyButton testId={`copy-items-${o.id}`} label="Copy items"
                          text={items.map(it => `${[it.name, it.strength].filter(Boolean).join(' ')} - Qty ${it.qty}`).join('\n')} />
                      </div>
                      <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] divide-y divide-neutral-100 dark:divide-neutral-800">
                        {items.map((it, i) => (
                          <div key={i} className="flex items-start justify-between gap-3 p-3">
                            <div className="min-w-0">
                              <div className="text-[13.5px] font-medium text-neutral-900 dark:text-neutral-100 truncate">{it.name}</div>
                              <div className="text-[12px] text-neutral-500">{it.strength}{it.packaging ? ' · ' + it.packaging : ''}</div>
                              <div className="text-[11.5px] text-neutral-500 mt-0.5">Qty {it.qty} • ${Number(it.price).toFixed(2)} each</div>
                            </div>
                            <div className="text-[13.5px] font-medium text-neutral-900 dark:text-neutral-100 whitespace-nowrap">${(Number(it.price) * Number(it.qty)).toFixed(2)}</div>
                          </div>
                        ))}
                      </div>
                      <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] p-4 mt-3 text-[13.5px] space-y-1.5">
                        <div className="flex justify-between"><span className="text-neutral-500">Subtotal</span><span>${Number(o.subtotal || 0).toFixed(2)}</span></div>
                        <div className="flex justify-between"><span className="text-neutral-500">Shipping</span><span>${Number(o.shipping || 0).toFixed(2)}</span></div>
                        {o.discountAmount ? (
                          <div className="flex justify-between text-emerald-600"><span>Discount{o.coupon_code ? ` (${o.coupon_code})` : ''}</span><span>- ${Number(o.discountAmount || 0).toFixed(2)}</span></div>
                        ) : null}
                        <div className="flex justify-between pt-2 mt-1 border-t border-neutral-100 dark:border-neutral-800"><span className="font-medium">Total</span><span className="font-semibold">${Number(o.total || 0).toFixed(2)}</span></div>
                        {o.currency && o.currency !== 'USD' && o.local_totals && (
                          <div data-testid={`order-local-breakdown-${o.id}`} className="mt-1 text-[11.5px] text-neutral-500">Customer paid in {o.currency}: {SYMBOLS[o.currency]}{Number(o.local_totals.total).toFixed(2)} (rate {Number(o.fx_rate).toFixed(4)})</div>
                        )}
                      </div>
                    </div>
                    <div className="md:col-span-2 border-t border-neutral-200 dark:border-neutral-800 pt-5">
                      <AdminOrderPortal order={o} headers={headers}
                        onOrderChange={(d) => setOrders(prev => prev.map(x => (x.id === d.id ? { ...x, ...d } : x)))} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {filtered.length > 0 && (
            <Pagination page={page} pageSize={pageSize} total={filtered.length} onPage={goPage} onPageSize={setPageSize} />
          )}
        </div>

        <p className="text-[11.5px] text-neutral-500 text-center pt-2">
          {orders.length} order(s) loaded. Status changes are saved immediately.
        </p>
      </main>
      )}
    </div>
  );
};

export default AdminDashboard;
